import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import {useTranslations} from "use-intl";

import { Nullable } from '@tager/web-core';

import { getAliasString } from '@/utils/common';
import { useTypedSelector } from '@/store/store';
import { selectProductResource } from '@/store/reducers/pages/product/product';
import { colors } from '@/constants/theme';
import Link from '@/components/Link';
import {
  FullProductType,
  ProductParamsValueType,
  ProductSize,
} from '@/typings/model';
import TextWithIcon from '@/components/TextWithIcon';
import Material from '@/modules/Product/components/Main/components/Brand/Material';

type BrandType = {
  label: string;
  text: string | number;
  to?: string;
};

type Size = {
  label: string;
  text: string;
};

type Props = {
  product: Nullable<FullProductType>;
};

function Brand({ product }: Props) {
  const t = useTranslations();
  
  const dataList = useMemo(() => {
    const brand: BrandType[] = [];
    const size: Size[] = [];
    const sleepingSize: Size[] = [];

    if (product?.brand) {
      brand.push({
        label: t('manufacturer'),
        text: product.brand.name,
        to: '/brands/' + product.brand.urlAlias,
      });
    }
    if (product?.collection) {
      brand.push({
        label: t('collection'),
        text: product.collection.name,
        to: '/collections/' + product.collection.urlAlias,
      });
    }

    if (product?.params) {
      product.params.forEach((param) => {
        if (param.field.name === 'MEHANIZM-TRANSFORMACII') {
          brand.push({
            label: param.field.label,
            text: (param.value as ProductParamsValueType)?.label,
          });
        }

        if (param.field.name === 'DLINA') {
          size.push({
            label: param.field.label,
            text: Math.ceil((param.value as number) / 10) + ' ' + t('dimensions.sm'),
          });
        }
        if (param.field.name === 'VYSOTA') {
          size.push({
            label: param.field.label,
            text: Math.ceil((param.value as number) / 10) + ' ' + t('dimensions.sm'),
          });
        }
        if (param.field.name === 'GLUBINA') {
          size.push({
            label: param.field.label,
            text: Math.ceil((param.value as number) / 10) + ' ' + t('dimensions.sm'),
          });
        }
        if (param.field.name === 'DLINA-SPALNOGO-MESTA') {
          sleepingSize.push({
            label: t('params.param_6'),
            text: Math.ceil((param.value as number) / 10) + ' ' + t('dimensions.sm'),
          });
        }
        if (param.field.name === 'SHIRINA-SPALNOGO-MESTA') {
          sleepingSize.push({
            label:t('params.param_9'),
            text: Math.ceil((param.value as number) / 10) + ' ' + t('dimensions.sm'),
          });
        }
      });
    }

    return { brand, size, sleepingSize };
  }, [product]);

  return (
    <Component>
      <Top>
        {(dataList.brand ?? []).map(({ text, to, label }, index) => {
          if (to) {
            return (
              <Item key={index}>
                <Label>{label}: </Label>
                <Link to={to}>
                  <Text hasColor>{text}</Text>
                </Link>
              </Item>
            );
          }
          return (
            <Item key={index}>
              <Label>{label}: </Label>
              <Text>{text}</Text>
            </Item>
          );
        })}
      </Top>
      <Middle>
        {!!(product?.size as ProductSize[]).length ? (
          <>
            <TextWithIcon
              note={t('params.param_3')}
              icon="size"
              fontWeight={600}
              marginTop={10}
            />
            <ItemWrapper>
              {dataList.size.map(({ text, label }, index) => (
                <ItemColumn key={index}>
                  <Label>{label}</Label>
                  <Text>{text}</Text>
                </ItemColumn>
              ))}
            </ItemWrapper>
          </>
        ) : null}

        {!!(product?.matressSize as ProductSize[]).length ? (
          <>
            <TextWithIcon
              note={t('params.param_2')}
              icon="size"
              fontWeight={600}
              marginTop={10}
            />
            <ItemWrapper>
              {dataList.sleepingSize.map(({ text, label }, index) => (
                <ItemColumn key={index}>
                  <Label>{label}</Label>
                  <Text>{text}</Text>
                </ItemColumn>
              ))}
            </ItemWrapper>
          </>
        ) : null}
      </Middle>
      <Bottom>
        <Material />
      </Bottom>
    </Component>
  );
}

export default Brand;

const Component = styled.div`
  margin-top: 20px;
`;

const Top = styled.div`
  & > div:not(:first-child) {
    margin-top: 5px;
  }
`;
const Item = styled.div``;
const ItemColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const ItemWrapper = styled.div`
  margin-top: 7px;
  max-width: 480px;
  display: flex;
`;
const Label = styled.span`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.gray400};
`;
const Text = styled.span<{ hasColor?: boolean }>`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  ${({ hasColor }) =>
    hasColor
      ? css`
          color: ${colors.primary};
          font-weight: 600;
        `
      : css`
          ${colors.black}
        `};
`;
const Middle = styled.div``;
const Bottom = styled.div`
  margin-top: 15px;
  button {
    margin: 0 auto;
  }
`;
